import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

const fmt = (n: number) => `LKR ${Number(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

interface Asset {
  id: number;
  name: string;
  description?: string;
  asset_account_code: string;
  accum_dep_account_code: string;
  dep_expense_account_code: string;
  purchase_date: string;
  purchase_cost: number;
  salvage_value: number;
  useful_life_months: number;
  status: string;
  total_depreciated: number;
  last_depreciation_date?: string;
  monthly_depreciation: number;
  remaining_depreciable: number;
  book_value: number;
}

const statusColor: Record<string, string> = {
  active: '#2e7d32',
  fully_depreciated: '#f57c00',
  disposed: '#c62828',
};

const Depreciation: React.FC = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [error] = useState('');
  const [success] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/depreciation/assets');
      setAssets(res.data.data || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pctDepreciated = (a: Asset) => {
    const dep = parseFloat(String(a.purchase_cost)) - parseFloat(String(a.salvage_value));
    if (!dep) return 0;
    return Math.min(100, (parseFloat(String(a.total_depreciated)) / dep) * 100);
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting')}>← Back</BackButton>
        <div>
          <h1>Depreciation Schedules</h1>
          <Subtitle>Manage fixed assets and run straight-line depreciation with automatic journal posting</Subtitle>
        </div>
        <AddButton onClick={() => setAddOpen(true)}>+ Add Asset</AddButton>
      </Header>

      {error && <ErrorAlert>{error}</ErrorAlert>}
      {success && <SuccessAlert>{success}</SuccessAlert>}

      <InfoBanner>
        ℹ️ Straight-line depreciation: (Cost - Salvage Value) / Useful Life. Run monthly depreciation to post automatic journal entries.
      </InfoBanner>

      {loading ? (
        <EmptyState>Loading assets...</EmptyState>
      ) : assets.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏗️</EmptyIcon>
          <p>No fixed assets registered yet. Click "+ Add Asset" to get started.</p>
        </EmptyState>
      ) : (
        <AssetsGrid>
          {assets.map((asset) => (
            <AssetCard key={asset.id}>
              <AssetHeader>
                <AssetName>{asset.name}</AssetName>
                <StatusBadge color={statusColor[asset.status] || '#999'}>
                  {asset.status.replace('_', ' ').toUpperCase()}
                </StatusBadge>
              </AssetHeader>

              {asset.description && <AssetDesc>{asset.description}</AssetDesc>}

              <AssetDetails>
                <DetailRow>
                  <DetailLabel>Purchase Date:</DetailLabel>
                  <DetailValue>{asset.purchase_date}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Purchase Cost:</DetailLabel>
                  <DetailValue>{fmt(asset.purchase_cost)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Salvage Value:</DetailLabel>
                  <DetailValue>{fmt(asset.salvage_value)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Useful Life:</DetailLabel>
                  <DetailValue>{asset.useful_life_months} months</DetailValue>
                </DetailRow>
              </AssetDetails>

              <Divider />

              <DepreciationInfo>
                <DetailRow>
                  <DetailLabel>Monthly Depreciation:</DetailLabel>
                  <DetailValue strong>{fmt(asset.monthly_depreciation)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Total Depreciated:</DetailLabel>
                  <DetailValue>{fmt(asset.total_depreciated)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Book Value:</DetailLabel>
                  <DetailValue strong color="#1976d2">
                    {fmt(asset.book_value)}
                  </DetailValue>
                </DetailRow>
                {asset.last_depreciation_date && (
                  <DetailRow>
                    <DetailLabel>Last Run:</DetailLabel>
                    <DetailValue>{asset.last_depreciation_date}</DetailValue>
                  </DetailRow>
                )}
              </DepreciationInfo>

              <ProgressBar>
                <ProgressFill width={pctDepreciated(asset)} />
              </ProgressBar>
              <ProgressLabel>{pctDepreciated(asset).toFixed(1)}% Depreciated</ProgressLabel>

              <AssetActions>
                <ActionButton disabled={asset.status !== 'active'}>
                  Run Depreciation
                </ActionButton>
                <ActionButton secondary>View Schedule</ActionButton>
              </AssetActions>
            </AssetCard>
          ))}
        </AssetsGrid>
      )}

      {/* Add Asset Modal - Simplified for now */}
      {addOpen && (
        <Modal>
          <ModalOverlay onClick={() => setAddOpen(false)} />
          <ModalContent>
            <ModalHeader>
              <h2>Add Fixed Asset</h2>
              <CloseButton onClick={() => setAddOpen(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <p>Add asset form would go here...</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                This would include fields for asset name, purchase cost, salvage value, useful life, and account codes.
              </p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setAddOpen(false)}>Cancel</CancelButton>
              <SaveButton onClick={() => setAddOpen(false)}>Save Asset</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }

  & > div {
    flex: 1;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const AddButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const SuccessAlert = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const InfoBanner = styled.div`
  background: #e3f2fd;
  color: #1565c0;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #bbdefb;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const AssetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
`;

const AssetCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const AssetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const AssetName = styled.h3`
  font-size: 18px;
  color: #333;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  background: ${props => props.color}20;
  color: ${props => props.color};
`;

const AssetDesc = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
`;

const AssetDetails = styled.div`
  margin-bottom: 16px;
`;

const DepreciationInfo = styled.div`
  margin-bottom: 16px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span<{ strong?: boolean; color?: string }>`
  font-weight: ${props => (props.strong ? 600 : 400)};
  color: ${props => props.color || '#333'};
  font-family: ${props => (props.strong ? 'monospace' : 'inherit')};
`;

const Divider = styled.div`
  height: 1px;
  background: #eee;
  margin: 16px 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ width: number }>`
  width: ${props => props.width}%;
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-bottom: 16px;
`;

const AssetActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button<{ secondary?: boolean }>`
  flex: 1;
  padding: 10px;
  background: ${props => (props.secondary ? 'white' : '#667eea')};
  color: ${props => (props.secondary ? '#667eea' : 'white')};
  border: ${props => (props.secondary ? '2px solid #667eea' : 'none')};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => (props.secondary ? '#667eea' : '#5568d3')};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalContent = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;

  h2 {
    margin: 0;
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  color: #999;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: calc(90vh - 160px);
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 24px;
  border-top: 1px solid #eee;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: white;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
  }
`;

export default Depreciation;
